import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Browser from '@wetransfer/concorde-browser';

import Message from './message';

import AutoSuggest from './autosuggest';
import LockedSender from './locked-sender';

import I18n from '@module/i18n';

import Actions from '@app/store/action';
import Helper from '@app/helper';

import Icon from '@component/icon';
import TextField from '@component/form/element/textfield';

export class Fields extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      recipientSummary: this.props.recipientCount,
      selectedRecipient: false,
      senderEmail: props.transfer.sender,
    };

    this.addRecipient = this.addRecipient.bind(this);
    this.deleteRecipient = this.deleteRecipient.bind(this);
    this.focusSender = this.focusSender.bind(this);
    this.navigateSelectedRecipient = this.navigateSelectedRecipient.bind(this);
    this.onRecipientBlur = this.onRecipientBlur.bind(this);
    this.renderRecipientEntry = this.renderRecipientEntry.bind(this);
    this.resetSelectedRecipient = this.resetSelectedRecipient.bind(this);
    this.setMessage = this.setMessage.bind(this);
    this.setSender = this.setSender.bind(this);
    this.showAllRecipients = this.showAllRecipients.bind(this);
    this.toggleRecipients = this.toggleRecipients.bind(this);
    this.showLockedSenderTip = this.showLockedSenderTip.bind(this);
  }

  componentDidMount() {
    const { loggedIn, isVerified, hasSkippedVerification } = this.props.user;
    if (loggedIn && isVerified && !hasSkippedVerification) {
      this.props.setSender(this.props.user.email);
    }
  }

  addRecipient(email, method = 'typed', nextNode = 'recipientNode') {
    this.props.addRecipient(email, method);
    if (nextNode && this[nextNode] && this[nextNode].wrappedInstance) {
      this[nextNode].wrappedInstance.focus();
    }
  }

  focusSender() {
    this.toggleRecipients();
    if (this.props.user?.loggedIn) {
      return;
    }

    if (this.props.isRecipientLocked) {
      this.props.pushTransferNotification({
        text: 'TipSenderRequireVerificationLockedRecipient',
        timeout: 10000,
      });
    } else {
      this.props.pushTransferNotification({
        text: 'TipSenderRequireVerification',
        timeout: 10000,
      });
    }
  }

  navigateSelectedRecipient(e) {
    if (this.state.selectedRecipient === false) {
      return;
    }

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        this.setState({
          selectedRecipient: Math.max(0, this.state.selectedRecipient - 1),
        });
        e.preventDefault();
        return false;

      case 'ArrowRight':
      case 'ArrowDown':
        this.setState({
          selectedRecipient: Math.min(
            this.props.transfer.recipients.length - 1,
            this.state.selectedRecipient + 1
          ),
        });
        e.preventDefault();
        return false;
    }
  }

  resetSelectedRecipient() {
    // Reset selected recipient if we proceed with something else
    if (this.state.selectedRecipient !== false) {
      this.setState({ selectedRecipient: false });
    }
  }

  deleteRecipient() {
    if (this.state.selectedRecipient === false) {
      // Set selection to the last recipient
      this.setState({
        selectedRecipient: this.props.transfer.recipients.length - 1,
      });
    } else {
      // If we have a selection, reset it and remove it from the transfer
      this.setState({ selectedRecipient: false });
      this.onDeleteRecipient(
        this.props.transfer.recipients[this.state.selectedRecipient],
        'keyboard'
      );
      this.recipientNode.focus();
    }
  }

  onRecipientBlur() {
    this.resetSelectedRecipient();
  }

  // This event gets called for every keypress in the input field,
  // however only responds to Enter. Then 'validates' e-mail quickly
  // to finally set the sender on the transfer.
  setSender(e) {
    this.setState({ senderEmail: e.target.value });
    if (e.key !== 'Enter' && e.type !== 'blur') {
      return;
    }
    if (
      !this.props.transfer.sender &&
      !e.target.value.replace(/\s* /g, '') &&
      e.type === 'blur'
    ) {
      return;
    }

    this.props.setSender(e.target.value);

    if (e.type === 'blur') {
      return;
    }
    this.messageNode.focus();
    e.preventDefault();
    return false;
  }

  // This event gets called on blur and adds a message to the transfer.
  setMessage(value) {
    this.props.setMessage(value);
  }

  // Remove a recipient
  onDeleteRecipient(email) {
    this.props.removeRecipient(email);
  }

  // Show or hide recipient summary
  toggleRecipients() {
    const { transfer } = this.props;

    // Interaction is happening!
    if (this.props.onInteraction) {
      this.props.onInteraction();
    }

    // Do we hide or show the recipient summary?
    if (transfer.recipients.length > 0 && !this.state.recipientSummary) {
      this.setState({ recipientSummary: true });
    } else if (
      transfer.recipients.length === 0 &&
      this.state.recipientSummary
    ) {
      this.setState({ recipientSummary: false });
    }
  }

  // Show all recipients (hide summary)
  showAllRecipients() {
    // Interaction is happening!
    if (this.props.onInteraction) {
      this.props.onInteraction();
    }
    this.setState({ recipientSummary: false });
  }

  showLockedSenderTip() {
    this.props.pushTransferNotification({ text: 'TipSenderLocked' });
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.recipientNode) {
      return;
    }

    // Only the first time you add files, we want to focus recipient afterwards
    if (
      this.props.fileCount > prevProps.fileCount &&
      prevProps.fileCount === 0
    ) {
      if (this.state.recipientSummary) {
        this.showAllRecipients();
      } else {
        this.recipientNode.focus();
      }
    }

    // We want to show the recipients again
    if (prevState.recipientSummary && !this.state.recipientSummary) {
      this.recipientNode.focus();
    }

    // We removed a recipient, focus the field again
    if (prevProps.recipientCount > this.props.recipientCount) {
      this.showAllRecipients();

      // NOTE: Because deleting by click will remove the focus we have to restore it,
      // I tried my best avoiding this, but I cannot get it to focus in the React loop
      // without this hacky feeling
      this.recipientFocusTimeout = setTimeout(() => {
        this.recipientNode.focus();
      }, 1);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.recipientFocusTimeout);
  }

  renderRecipientEntry(recipient, index) {
    let className = 'uploader__recipient';

    // Is this one currently selected (for backspace deletion)
    if (index === this.state.selectedRecipient) {
      className += ' uploader__recipient--selected';
    }

    return (
      <li className={className} key={recipient}>
        {recipient}
        <div
          className="uploader__remove-recipient"
          onMouseDown={this.onDeleteRecipient.bind(this, recipient)}
        >
          <Icon type="Remove" />
        </div>
      </li>
    );
  }

  renderRecipients(transfer) {
    if (this.state.recipientSummary && transfer.recipients.length) {
      let buttonClassName = 'uploader__recipient';

      // For a single recipient we want to update the className
      if (transfer.recipients.length === 1) {
        buttonClassName += ' uploader__recipient--hidden';
      }

      const classname = `uploader__recipient-summary uploader__recipient-summary--${transfer.recipients.length}-recipients`;

      // Return our recipient summary element
      return (
        <div className={classname} onClick={this.showAllRecipients}>
          <span className="uploader__first-recipient">
            {transfer.recipients[0]}
          </span>
          {transfer.recipients.length > 1 ? ',' : null}
          <button className={buttonClassName} onFocus={this.showAllRecipients}>
            {I18n.t('transfer.form.other_recipients', {
              count: transfer.recipients.length - 1,
            })}
          </button>
        </div>
      );
    }

    // Attempt to render all recipient entries in a nice list÷
    let recipients = null;
    if (transfer.recipients.length) {
      recipients = (
        <ul className="uploader__recipients">
          {transfer.recipients.map(this.renderRecipientEntry)}
        </ul>
      );
    }

    const autosuggestProps = {
      key: 'recipient',
      ref: (node) => (this.recipientNode = node),
      panel: this.props.panel,
      transfer: this.props.transfer,
      recipientCount: this.props.recipientCount,
      onChange: this.resetSelectedRecipient,
      onFocus: this.props.onInteraction,
      onBlur: this.onRecipientBlur,
      onRecipient: this.addRecipient,
      onDelete: this.deleteRecipient,
      onKeyPress: this.navigateSelectedRecipient,
      placeholder: I18n.t('transfer.form.to'),
    };

    if (!this.state.recipientSummary && this.props.recipientCount > 0) {
      autosuggestProps.autoFocus = true;
    }

    const autosuggest = <AutoSuggest {...autosuggestProps} />;

    // Return a recipient list (if applicable) and the input textfield
    return [recipients, autosuggest];
  }

  // Render the Message component field
  renderMessage(transfer) {
    return (
      <Message
        key="message"
        name="message"
        ref={(node) => (this.messageNode = node)}
        defaultValue={transfer.message}
        panel={this.props.panel}
        transfer={transfer}
        placeholder={I18n.t('transfer.form.message')}
        onChange={this.setMessage}
        onFocus={this.toggleRecipients}
      />
    );
  }

  // Render the Sender (From) textfield
  renderSender() {
    const classNames = ['uploader__sender'];
    const { user, clearTransferNotification } = this.props;

    if (!Browser.isMobile) {
      classNames.push('textfield--borderbottom');
    }

    if (Helper.isSenderLocked(user)) {
      return (
        <LockedSender
          sender={user.email}
          onMouseOver={this.showLockedSenderTip}
          onFocus={this.showLockedSenderTip}
          onMouseLeave={clearTransferNotification}
        />
      );
    }

    return (
      <TextField
        key="sender"
        hideLabelOnInput={true}
        fieldRef={(node) => (this.senderNode = node)}
        type="email"
        name="email"
        className={classNames.join(' ')}
        value={this.state.senderEmail}
        label={I18n.t('transfer.form.from')}
        onChange={this.setSender}
        onFocus={this.focusSender}
        onBlur={this.setSender}
      />
    );
  }

  // Render all the form fields that are needed
  render() {
    const { transfer, type, isRecipientLocked } = this.props;
    const messageField = this.renderMessage(transfer);

    // Link transfers only have a message
    if (type === 'link') {
      return messageField;
    }

    const focus = document.querySelector(
      '.uploader__recipientsContainer input:focus'
    )
      ? 'uploader__recipientsContainer-focused'
      : '';
    // Return our field container (with fields!)
    return (
      <div className="uploader__fields">
        {!isRecipientLocked && (
          <div className={`uploader__recipientsContainer ${focus}`}>
            {this.renderRecipients(transfer)}
          </div>
        )}
        {this.renderSender()}
        {messageField}
      </div>
    );
  }
}

Fields.propTypes = {
  addRecipient: PropTypes.func.isRequired,
  clearTransferNotification: PropTypes.func.isRequired,
  fileCount: PropTypes.number,
  notification: PropTypes.object,
  onInteraction: PropTypes.func,
  panel: PropTypes.object,
  pushTransferNotification: PropTypes.func.isRequired,
  recipientCount: PropTypes.number,
  removeRecipient: PropTypes.func.isRequired,
  setMessage: PropTypes.func.isRequired,
  setSender: PropTypes.func.isRequired,
  transfer: PropTypes.object,
  user: PropTypes.object,
  type: PropTypes.string,
  isRecipientLocked: PropTypes.bool,
};

const mapDispatchToProps = {
  addRecipient: Actions.Transfer.addRecipient,
  clearTransferNotification: Actions.Notification.clearTransfer,
  pushTransferNotification: Actions.Notification.pushTransfer,
  removeRecipient: Actions.Transfer.removeRecipient,
  setMessage: Actions.Transfer.setMessage,
  setSender: Actions.Transfer.setSender,
};

const mapStateToProps = ({ user, notification }) => ({ user, notification });
export default connect(mapStateToProps, mapDispatchToProps)(Fields);
